import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import InfoBannerCst from "@components/InfoBannerCst";
import { useNavigate, useParams } from "react-router-dom";
import { Download, Pencil, RotateCcw, Search, Trash2 } from "lucide-react";
// import IconMegaphone from "@components/CustomIcons/IconMegaphone";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import InputCst from "@components/InputCst/InputCst";
import TabsCst from "@components/TabsCst";
import ListItemCardCst from "@components/ListItemCardCst";
import PaginatorCst from "@components/PaginatorCst";
import FiltersAccordionToggle from "@components/FiltersAccordionCst/FiltersAccordionToggle";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL, downloadFile } from "@utils/utilsApi";
import { MethodType } from "src/services/type";
import { PagesID } from "@utils/utilsConfigurations";
import { mapEntityToCard } from "@pages/Contents/V2/Corporate/Catalog/mapEntityToCard";
import { mapAggregatedType } from "@pages/Contents/V2/Corporate/Catalog/types";
import { isClassroomActivity } from "@utils/cardActions";
import {
  formatEnrollmentDate,
  formatDate,
} from "@pages/Contents/V2/Corporate/Catalog/UsersAndGroups/config/enrollmentDates";
import { normalizeEnrolledLo } from "../normalizeEnrolledLo";
import { downloadGroupLoCsv } from "../downloadGroupLoCsv";
import { buildGroupLoTabs, buildGroupLoTypeFilter } from "../groupLoTabs";
import { formatEnrollTypeLabel } from "@pages/Contents/V2/Corporate/Catalog/UsersAndGroups/config/enrollTypeMap";
import { buildExpirationBadge } from "@pages/Contents/V2/Corporate/Catalog/UsersAndGroups/config/expirationBadge";
import {
  buildConfigFromEnrollment,
  disabledByPriority,
} from "@pages/Contents/V2/Corporate/Catalog/UsersAndGroups/components/EditEnrollmentModal";
// import SolicitConfirmDialog, {
// SolicitTarget,
// } from "@pages/Contents/V2/Corporate/Catalog/UsersAndGroups/components/SolicitConfirmDialog";
import { PERMISSIONS, usePermissions } from "@utils/permissions";
import ConfirmDialogCst from "@components/ConfirmDialogCst";
import OutcomeModalV2Cst from "@components/OutcomeModalV2Cst";
import EnrollLOModal from "@pages/Contents/V2/Corporate/UsersGroups/components/EnrollLOModal";
import RefreshPerimeterDialog, {
  RefreshPerimeterTarget,
} from "../components/RefreshPerimeterDialog";
import { fetcher } from "src/services/config";
import { openToast } from "@utils/toastStore";
import { mapUserToCard } from "../mapUserToCard";
import { useGroupMembersDetails } from "../useGroupMembersDetails";
import {
  DetailAccordion,
  SectionHeader,
  searchBoxSx,
} from "../components/detailLayout";
import { useScrollToHash } from "@hooks/useScrollToHash";
import { LO_SECTION_ANCHOR, SECTION_ANCHOR_SCROLL_MARGIN } from "../types";
import { useRuleDataset } from "../DynamicGroupWizard/useRuleDataset";
import {
  DynRule,
  DynRuleSet,
  parseRuleOperator,
} from "../DynamicGroupWizard/types";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import PageHeaderV2Cst from "@components/PageHeaderV2Cst";

const PAGE_SIZE = 10;

const RulesSummary: React.FC<{ sets: DynRuleSet[] }> = ({ sets }) => {
  const { t } = useTranslation("group-wizard-v2");
  const { t: tDetail } = useTranslation("group-detail-v2");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();
  const { fieldOptions, conditionOptions } = useRuleDataset(corporateId);

  const labelOf = (
    options: { value: string; label: string }[],
    value: string,
  ) => options.find((o) => o.value === value)?.label || value;

  if (!sets || sets.length === 0 || sets.every((s) => !s.rules?.length)) {
    return (
      <Typography sx={{ color: theme.palette.text.primary }}>
        {tDetail("rules.empty")}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {sets.map((set, si) => {
        const setLogic = parseRuleOperator(set.operator || "AND").logic;
        return (
          <Box
            key={si}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "8px",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Typography sx={{ fontSize: "13px", fontWeight: 700 }}>
              {t("rules.ruleSet")} {si + 1} ·{" "}
              {setLogic === "OR" ? t("rules.any") : t("rules.all")}
            </Typography>
            {(set.rules || []).map((rule: DynRule, ri: number) => {
              const { exclude } = parseRuleOperator(rule.operator || "AND");
              return (
                <Box
                  key={ri}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <Chip
                    size="small"
                    label={exclude ? t("rules.exclude") : t("rules.include")}
                    color={exclude ? "error" : "success"}
                    variant="outlined"
                  />
                  <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                    {labelOf(fieldOptions, rule.condition_field)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: theme.palette.text.primary,
                    }}
                  >
                    {labelOf(conditionOptions, rule.condition)}
                  </Typography>
                  {rule.value && (
                    <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                      {rule.value}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

const DynamicGroupDetail: React.FC = () => {
  const { t } = useTranslation("group-detail-v2");
  const { t: tCatalog } = useTranslation("catalog-v2");
  const { t: tInput } = useTranslation("input");
  const { t: tEnroll } = useTranslation("users-and-groups");
  const { t: tGroups } = useTranslation("users-groups-v2");
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: corporateId = "", groupId = "" } = useParams();
  const { can } = usePermissions();
  const canUpdateEnrollment = can(PERMISSIONS.enrollment.update);
  const canWriteDynamicGroups = can(PERMISSIONS.groups.dynamic.write);
  const canReadDynamicGroups = can(PERMISSIONS.groups.dynamic.read);
  const [refreshPerimeterTarget, setRefreshPerimeterTarget] =
    useState<RefreshPerimeterTarget | null>(null);

  const dynamicGroupsUrl = ServicesURL.dynamicGroups.replace(
    ":idCorporate",
    corporateId,
  );
  const { dataDetail: groupDetail } = useFetchDetail(
    dynamicGroupsUrl,
    groupId,
    PagesID["corporate.groups-dynamic.edit"],
  );

  useScrollToHash(!!groupDetail);

  // Sezione "Iniziative associate" nascosta
  // const initiativesUrl = `${ServicesURL.getEnrollmentMassive}?corporateId=${corporateId}&groupId=${groupId}`;
  // const { items: initiatives, isLoading: loadingInit } = usePaginatedList(
  //   initiativesUrl,
  //   { page: 1, pageSize: PAGE_SIZE },
  // );

  // Utenti del gruppo dinamico
  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(PAGE_SIZE);
  // const [usersSearch, setUsersSearch] = useState("");
  const [usersFilters, setUsersFilters] = useState<Record<string, any>>({});
  const usersUrl = ServicesURL.getDynamicGroupsUsers
    .replace(":idCorporate", corporateId)
    .replace("::groupId", groupId);
  const {
    items: users,
    totalItems: usersTotal,
    totalPages: usersPages,
    isLoading: loadingUsers,
    mutate: mutateUsers,
  } = usePaginatedList(
    usersUrl,
    {
      page: usersPage,
      pageSize: usersPageSize,
      // search: usersSearch || undefined,
      fieldFilters: usersFilters,
    },
    { responseKey: "response" },
  );

  const usersPageIds = useMemo(
    () => users.map((u: any) => u.id_user ?? u.id ?? u.user_id),
    [users],
  );
  const { detailsById: userDetailsById } = useGroupMembersDetails(usersPageIds);

  const [isDownloadingUsersCsv, setIsDownloadingUsersCsv] = useState(false);

  const handleDownloadUsersCsv = async () => {
    setIsDownloadingUsersCsv(true);
    try {
      const res = await downloadFile(
        ServicesURL.downloadDynamicGroupUsers
          .replace(":idCorporate", corporateId)
          .replace(":idGroup", groupId),
      );
      if (res instanceof Error) {
        openToast({
          severity: "error",
          message: String(t("users.csvError")),
        });
      }
    } finally {
      setIsDownloadingUsersCsv(false);
    }
  };

  // Oggetti formativi associati
  const [loTab, setLoTab] = useState("all");
  const [loPage, setLoPage] = useState(1);
  const [loPageSize, setLoPageSize] = useState(PAGE_SIZE);
  const [loSearch, setLoSearch] = useState("");
  // P35 (allineare V2 a v1): LO del gruppo visibili e caricati solo con
  // learning.enrollment.read + learning_object.corporate.visibility, come la
  // route v1 ":idGroup/learning-objects" (corporate && corporate.catalog &&
  // learning.enrollment.read; corporate è già richiesto dalla route V2).
  const canViewGroupLo = can([
    PERMISSIONS.enrollment.read,
    PERMISSIONS.catalog.corporateVisibility,
  ]);
  const loUrl = canViewGroupLo
    ? `${ServicesURL.getLOEnrolledWithEnrollInfo
        .replace(":corporateId", corporateId)
        .replace(":groupId", groupId)}?isStandalone=true`
    : null;
  const {
    items: los,
    totalItems: loTotal,
    totalPages: loPages,
    isLoading: loadingLo,
    mutate: mutateLos,
  } = usePaginatedList(loUrl, {
    page: loPage,
    pageSize: loPageSize,
    search: loSearch || undefined,
    searchField: "loTitle",
    fieldFilters: buildGroupLoTypeFilter(loTab),
  });

  const [deleteLoTarget, setDeleteLoTarget] = useState<any>(null);
  const [isDeletingLo, setIsDeletingLo] = useState(false);
  const [showPartialDeleteInfo, setShowPartialDeleteInfo] = useState(false);
  const warnDeleteLoProgress =
    !!deleteLoTarget &&
    (mapAggregatedType(deleteLoTarget.type) !== "activity" ||
      isClassroomActivity(
        deleteLoTarget.type || deleteLoTarget.learning_object_type,
      ));
  const [editGroupLoTarget, setEditGroupLoTarget] = useState<any>(null);
  const [groupEnrollSource, setGroupEnrollSource] = useState<any>(null);
  // const [solicitTarget, setSolicitTarget] = useState<SolicitTarget | null>(
  // null,
  // );
  // const canSolicit = can(PERMISSIONS.enrollment.reminderMassive);

  // const openSolicit = (entity: any) => {
  // if (!canSolicit) return;
  // setSolicitTarget({
  // scope: "group",
  // name: groupDetail?.name || `ID ${groupId}`,
  // corporateId,
  // objectId: entity.id,
  // objectType: mapAggregatedType(entity.type),
  // groupId,
  // groupType: "DYNAMIC",
  // });
  // };
  const [isLoadingGroupEnrollment, setIsLoadingGroupEnrollment] =
    useState(false);
  const [isDownloadingLoCsv, setIsDownloadingLoCsv] = useState(false);

  const handleDownloadLoCsv = async () => {
    if (!loUrl) return;
    setIsDownloadingLoCsv(true);
    try {
      await downloadGroupLoCsv(loUrl, groupId, t, loSearch.trim());
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(t("learningObjects.csvError")),
      });
    } finally {
      setIsDownloadingLoCsv(false);
    }
  };

  const confirmDeleteLo = async () => {
    if (!deleteLoTarget || !canUpdateEnrollment) return;
    setIsDeletingLo(true);
    try {
      const url = `${ServicesURL.postEnrollmentMassive}?corporateId=${corporateId}&groupId=${groupId}&groupType=DYNAMIC&objectId=${deleteLoTarget.id}`;
      const res = await fetcher(url, MethodType.DELETE);
      setDeleteLoTarget(null);
      mutateLos();
      setTimeout(() => mutateLos(), 1500);
      setTimeout(() => mutateLos(), 4000);
      if (res?.usersAlreadyCompleted === true) {
        setShowPartialDeleteInfo(true);
      } else {
        openToast({
          severity: "success",
          message: String(tEnroll("card.delete-confirm.success")),
        });
      }
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(tEnroll("card.delete-confirm.error")),
      });
    } finally {
      setIsDeletingLo(false);
    }
  };

  const openGroupEnrollmentEditor = async () => {
    if (!editGroupLoTarget) return;
    setIsLoadingGroupEnrollment(true);
    try {
      const url = `${ServicesURL.getEnrollment}?corporateId=${corporateId}&objectId=${editGroupLoTarget.id}&massiveGroupId=${groupId}&groupType=DYNAMIC&pageIndex=0&pageSize=20`;
      const res = await fetcher(url, MethodType.GET);
      const record = res?.enrollmentResponses?.[0];
      setGroupEnrollSource({
        ...editGroupLoTarget,
        ...record,
        id: editGroupLoTarget.id,
        title: editGroupLoTarget.title,
        entityType: mapAggregatedType(editGroupLoTarget.type),
      });
      setEditGroupLoTarget(null);
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(tEnroll("card.edit-group-confirm.fetch-error")),
      });
    } finally {
      setIsLoadingGroupEnrollment(false);
    }
  };

  const loTabs = buildGroupLoTabs(t);

  const goToUserDetail = (u: any) =>
    navigate(
      `/v2/corporate/${corporateId}/detail/users-groups/${u.id ?? u.user_id}`,
    );

  return (
    <AnimationFadeIn>
      <Box
        sx={{
          py: 3,
          pr: 3,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {/* Header */}
        <PageHeaderV2Cst>
          <Breadcrumb sticky={false} />
          <Typography
            sx={{ fontSize: "13px", color: theme.palette.text.primary, mt: 1 }}
          >
            ID: {groupId}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              mt: 2,
            }}
          >
            <Typography sx={{ fontSize: "28px", fontWeight: 700 }}>
              {groupDetail?.name || "-"}
            </Typography>
            {groupDetail?.description && (
              <Typography
                sx={{
                  fontSize: "14px",
                  color: theme.palette.text.primary,
                  opacity: 0.8,
                }}
              >
                {groupDetail.description}
              </Typography>
            )}
          </Box>
        </PageHeaderV2Cst>

        {/* Logiche di condizioni e regole */}
        <DetailAccordion title={t("rules.title")}>
          <RulesSummary sets={groupDetail?.sets || []} />
        </DetailAccordion>

        {/* TODO Sezione "Iniziative associate" nascosta */}
        {/*
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SectionHeader
            title={t("initiatives.title")}
            action={
              <Button
                variant="contained"
                startIcon={<Plus size={16} />}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  fontWeight: 700,
                }}
              >
                {t("initiatives.associate")}
              </Button>
            }
          />
          {loadingInit ? (
            <CircularProgress />
          ) : initiatives.length === 0 ? (
            <Typography sx={{ color: theme.palette.text.primary }}>
              {t("initiatives.empty")}
            </Typography>
          ) : (
            initiatives.map((row: any, index: number) => (
              <ListItemCardCst
                key={row.id ?? index}
                title={row.name || row.initiativeName || t("initiatives.item")}
                subtitle={{ id: row.id ?? row.enrollmentId }}
                badges={[]}
                actions={[]}
              />
            ))
          )}
        </Box>
        */}

        {/* Utenti */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SectionHeader
            title={t("users.title")}
            action={
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <ButtonSecondary
                  size="md"
                  startIcon={<RotateCcw size={16} />}
                  disabled={!canWriteDynamicGroups || !groupDetail}
                  onClick={() =>
                    setRefreshPerimeterTarget({
                      scope: "group",
                      group: { ...groupDetail, id: groupId },
                    })
                  }
                >
                  {tGroups("refresh-perimeter-dialog.button")}
                </ButtonSecondary>
                <ButtonSecondary
                  size="md"
                  startIcon={<Download size={16} />}
                  onClick={handleDownloadUsersCsv}
                  disabled={!canReadDynamicGroups || isDownloadingUsersCsv}
                >
                  {t("downloadCsv")}
                </ButtonSecondary>
              </Box>
            }
          />

          <InfoBannerCst>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 700, color: "inherit" }}
            >
              {t("users.dynamicNoticeTitle")}
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "inherit" }}>
              {t("users.dynamicNoticeText")}
            </Typography>
          </InfoBannerCst>

          {/* <Box sx={searchBoxSx}>
            <InputCst
              id="dyn-group-users-search"
              placeholder={t("search")}
              inputSearch
              value={usersSearch}
              onChange={(e: any) => {
                setUsersSearch(e.target.value);
                setUsersPage(1);
              }}
              startAdornment={
                <Search
                  size={16}
                  color={theme.palette.systemSecondary01}
                  style={{ marginRight: 8 }}
                />
              }
            />
          </Box> */}

          <FiltersAccordionToggle
            align="right"
            pageId={PagesID["corporate.groups-dynamic.users"]}
            value={usersFilters}
            onApply={(next) => {
              setUsersFilters(next);
              setUsersPage(1);
            }}
          />

          {loadingUsers ? (
            <CircularProgress />
          ) : users.length === 0 ? (
            <Typography sx={{ color: theme.palette.text.primary }}>
              {t("users.empty")}
            </Typography>
          ) : (
            users.map((u: any, index: number) => {
              const memberId = u.id_user ?? u.id ?? u.user_id;
              const normalizedUser = {
                ...u,
                ...userDetailsById.get(String(memberId)),
                id_user: memberId,
                external_id: u.external_id ?? u.externalId,
              };
              const cardProps = mapUserToCard(normalizedUser, {
                tInput,
                corporateId,
              });
              return (
                <ListItemCardCst
                  key={memberId ?? index}
                  {...cardProps}
                  actions={[
                    {
                      label: t("details"),
                      variant: "outlined",
                      onClick: () => goToUserDetail(u),
                    },
                  ]}
                />
              );
            })
          )}

          {usersPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <PaginatorCst
                page={usersPage}
                pageSize={usersPageSize}
                totalItems={usersTotal}
                onPageChange={setUsersPage}
                onPageSizeChange={(next) => {
                  setUsersPageSize(next);
                  setUsersPage(1);
                }}
              />
            </Box>
          )}
        </Box>

        {/* Oggetti formativi associati */}
        {canViewGroupLo && (
          <Box
            id={LO_SECTION_ANCHOR}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              scrollMarginTop: SECTION_ANCHOR_SCROLL_MARGIN,
            }}
          >
            <SectionHeader
              title={t("learningObjects.title")}
              action={
                <ButtonSecondary
                  size="md"
                  startIcon={<Download size={16} />}
                  onClick={handleDownloadLoCsv}
                  disabled={isDownloadingLoCsv || loTotal === 0}
                >
                  {t("downloadCsv")}
                </ButtonSecondary>
              }
            />

            <TabsCst
              tabs={loTabs}
              activeKey={loTab}
              onChange={(key) => {
                setLoTab(key);
                setLoPage(1);
              }}
            />

            <Box sx={searchBoxSx}>
              <InputCst
                id="dyn-group-lo-search"
                placeholder={t("search")}
                inputSearch
                value={loSearch}
                onChange={(e: any) => {
                  setLoSearch(e.target.value);
                  setLoPage(1);
                }}
                startAdornment={
                  <Search
                    size={16}
                    color={theme.palette.systemSecondary01}
                    style={{ marginRight: 8 }}
                  />
                }
              />
            </Box>

            {loadingLo ? (
              <CircularProgress />
            ) : los.length === 0 ? (
              <Typography sx={{ color: theme.palette.text.primary }}>
                {t("learningObjects.empty")}
              </Typography>
            ) : (
              los.map((raw: any, index: number) => {
                const entity = normalizeEnrolledLo(raw);
                const entityType = mapAggregatedType(entity.type);
                const expirationBadge = buildExpirationBadge(
                  entity,
                  String(tEnroll("card.expiration-badge.expired")),
                );
                const cardProps = mapEntityToCard(entity, entityType, {
                  t: tCatalog,
                  onDetails: () => {},
                });
                return (
                  <ListItemCardCst
                    key={entity.id ?? index}
                    {...cardProps}
                    details={{
                      row1: `${tEnroll(
                        "card.enrollment-date",
                      )}: ${formatEnrollmentDate(entity.enrollDate)}`,
                      row2: `${tEnroll(
                        "card.expiration-date",
                      )}: ${formatDate(entity.expirationDate)}`,
                    }}
                    detailsVariant="detailed"
                    badges={[
                      ...(cardProps.badges || []),
                      ...(entity.enrollType
                        ? [{ label: formatEnrollTypeLabel(entity.enrollType) }]
                        : []),
                      ...(expirationBadge ? [expirationBadge] : []),
                    ]}
                    actions={[]}
                    // S05: azioni sempre visibili, disabilitate senza permesso.
                    moreActions={[
                      {
                        label: String(
                          tEnroll("card.actions.edit-group-enrollment"),
                        ),
                        icon: <Pencil size={14} />,
                        onClick: () => setEditGroupLoTarget(entity),
                        disabled: !canUpdateEnrollment,
                      },
                      {
                        label: String(
                          tEnroll("card.actions.delete-group-enrollment"),
                        ),
                        icon: <Trash2 size={14} />,
                        onClick: () => setDeleteLoTarget(entity),
                        disabled: !canUpdateEnrollment,
                      },
                      // {
                      // label: String(tEnroll("card.actions.solicit")),
                      // icon: <IconMegaphone size={14} />,
                      // onClick: () => openSolicit(entity),
                      // disabled: !canSolicit,
                      // },
                    ]}
                  />
                );
              })
            )}

            {loPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <PaginatorCst
                  page={loPage}
                  pageSize={loPageSize}
                  totalItems={loTotal}
                  onPageChange={setLoPage}
                  onPageSizeChange={(next) => {
                    setLoPageSize(next);
                    setLoPage(1);
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* <SolicitConfirmDialog
        target={solicitTarget}
        onClose={() => setSolicitTarget(null)}
      /> */}

      <RefreshPerimeterDialog
        target={refreshPerimeterTarget}
        corporateId={corporateId}
        onClose={() => setRefreshPerimeterTarget(null)}
        onSuccess={mutateUsers}
      />

      <ConfirmDialogCst
        open={!!deleteLoTarget}
        title={String(tEnroll("card.delete-group-confirm.title"))}
        description={String(
          tEnroll(
            warnDeleteLoProgress
              ? "card.delete-group-confirm.description-progress"
              : "card.delete-group-confirm.description",
          ),
        )}
        confirmLabel={String(tEnroll("card.delete-confirm.confirm"))}
        cancelLabel={String(tEnroll("card.delete-confirm.cancel"))}
        confirmVariant="danger"
        isSubmitting={isDeletingLo}
        onClose={() => setDeleteLoTarget(null)}
        onConfirm={confirmDeleteLo}
      />

      <OutcomeModalV2Cst
        open={showPartialDeleteInfo}
        outcome={{ deleteGroupEnrollment: { code: "OK", message: undefined } }}
        successTitle={tEnroll("card.delete-group-partial.title")}
        description={tEnroll("card.delete-group-partial.description")}
        confirmLabel={tEnroll("card.delete-group-partial.back-to-group")}
        hideCloseButton
        onClose={() => setShowPartialDeleteInfo(false)}
        onConfirm={() => setShowPartialDeleteInfo(false)}
      />

      <ConfirmDialogCst
        open={!!editGroupLoTarget}
        title={String(tEnroll("card.edit-group-confirm.title"))}
        description={String(
          tEnroll("card.edit-group-confirm.description", { groupId }),
        )}
        confirmLabel={String(tEnroll("card.edit-group-confirm.confirm"))}
        cancelLabel={String(tEnroll("card.delete-confirm.cancel"))}
        isSubmitting={isLoadingGroupEnrollment}
        submittingLabel={String(tEnroll("card.edit-group-confirm.loading"))}
        onClose={() => setEditGroupLoTarget(null)}
        onConfirm={openGroupEnrollmentEditor}
      />

      <EnrollLOModal
        open={!!groupEnrollSource}
        onClose={() => setGroupEnrollSource(null)}
        subjectType="group"
        subject={{
          id: Number(groupId),
          name: groupDetail?.name || `ID ${groupId}`,
        }}
        groupType="DYNAMIC"
        corporateId={String(corporateId)}
        onSuccess={() => {
          mutateLos();
          setTimeout(() => mutateLos(), 1500);
          setTimeout(() => mutateLos(), 4000);
          setGroupEnrollSource(null);
        }}
        initialStep="config"
        initialSelection={
          groupEnrollSource
            ? [
                {
                  id: groupEnrollSource.id,
                  kind: groupEnrollSource.entityType,
                  title: groupEnrollSource.title ?? "",
                  entity: {},
                },
              ]
            : []
        }
        initialConfig={
          groupEnrollSource
            ? buildConfigFromEnrollment(groupEnrollSource)
            : undefined
        }
        extraDisabledEnrollTypes={disabledByPriority(
          groupEnrollSource?.enrollType,
        )}
      />
    </AnimationFadeIn>
  );
};

export default DynamicGroupDetail;
